import React, { useMemo } from 'react';
import type { WidgetSize } from '../../types/dashboard';
import { RED, YELLOW, GREEN } from '../../constants';
import { computeTradeLevelStats } from '../../lib/trades/tradeLevelStats';

interface DailyData {
  // minimal shape required by this component
  pnl: number;
  // add other fields from your real DailyData type as needed, e.g.:
  // date?: string;
}

interface ProfitFactorWidgetProps {
  dailyData: DailyData[];
  size: WidgetSize;
  /** Authoritative trade records; when provided, profit factor uses individual trades. */
  trades?: any[];
}

export const ProfitFactorWidget: React.FC<ProfitFactorWidgetProps> = ({ dailyData, size, trades }) => {
  const { grossProfit, grossLoss, hasTrades, profitFactor } = useMemo(() => {
    if (Array.isArray(trades) && trades.length > 0) {
      const stats = computeTradeLevelStats(trades);
      const loss = Math.abs(stats.grossLosingPnl);
      return {
        grossProfit: stats.grossWinningPnl,
        grossLoss: loss,
        hasTrades: stats.wins + stats.losses + stats.breakeven > 0,
        profitFactor: stats.profitFactor,
      };
    }
    const profit = dailyData
      .filter(d => d.pnl > 0)
      .reduce((acc, d) => acc + d.pnl, 0);
    const loss = Math.abs(dailyData
      .filter(d => d.pnl < 0)
      .reduce((acc, d) => acc + d.pnl, 0));
    return {
      grossProfit: profit,
      grossLoss: loss,
      hasTrades: dailyData.length > 0,
      profitFactor: loss > 0 ? (profit / loss) : null,
    };
  }, [dailyData, trades]);

  // Standard profit factor definition: Gross Profit / Gross Loss.
  // When there are zero losses or gross P&L is unavailable, display N/A.
  const isAvailable = hasTrades && grossLoss > 0 && profitFactor != null && Number.isFinite(profitFactor);
  const displayFactor = isAvailable ? profitFactor : 0;
  const markerPosition = Math.min(100, (displayFactor / 3) * 100);

  // Map the marker position (0-100) to a color class so the number matches the bar color.
  // Use red for left, orange for mid, green for right. Thresholds chosen for clear visual distinction.
  const getTextColor = (posPercent: number) => {
    if (!isAvailable) return 'text-zinc-400 dark:text-zinc-500';
    if (!Number.isFinite(posPercent)) return 'text-emerald-600';
    if (posPercent < 33) return 'text-red-500';
    if (posPercent < 66) return 'text-orange-500';
    return 'text-emerald-600';
  };

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-1 -mt-[3px]">
      <div
        data-testid="profit-factor-value"
        className={`-mt-1 font-bold ${size === 'mini' ? 'text-4xl' : 'text-4xl'} mb-0 ${getTextColor(markerPosition)}`}
      >
        {isAvailable ? profitFactor.toFixed(2) : 'N/A'}
      </div>

      <div className="text-[12px] text-zinc-400 mt-1">Gross Profit / Gross Loss</div>

      <div className="w-full mt-0">
        <div
          data-testid="profit-factor-scale"
          className="relative h-1.5 w-full rounded-full"
          style={{ background: `linear-gradient(to right, ${RED}, ${YELLOW}, ${GREEN})` }}
        >
          {isAvailable && (
            <div
              className="absolute top-[-2px] h-3.5 w-1 bg-zinc-900 dark:bg-zinc-100 border border-zinc-400 dark:border-zinc-500 rounded-sm"
              style={{ left: `calc(${markerPosition}% - 2px)` }}
            />
          )}
        </div>
        <div className="relative mt-1 flex justify-between text-[10px] leading-none text-zinc-500 dark:text-zinc-400">
          <span>0</span>
          <span>1</span>
          <span>2</span>
          <span>3+</span>
        </div>
      </div>
    </div>
  );
};
